import { emissionDate, type InvoiceDraft } from './draft';

/**
 * Fecha de emisión retroactiva: hasta 72 horas atrás (regla del SAT CFDI40102) y
 * nunca futura. Es la única fuente de esa ventana en el front.
 *
 * Contrato:
 * - `draft.backdatedTo` vacío es «hoy, al timbrar»: la fecha se resuelve al emitir
 *   (`emissionDate`), igual que antes de que existiera esta opción.
 * - Con valor es un instante en UTC, elegido en la hora del dispositivo. Viaja como
 *   la de hoy: el backend lo corre al huso del lugar de expedición.
 * - Nunca se cambia en silencio por otra fecha. Una que caducó se señala y frena el
 *   timbrado; solo al retomar un borrador guardado regresa a hoy, y con aviso.
 *
 * El fiscal-engine valida la misma ventana y responde 422 si se cruza. Las holguras
 * de aquí existen para que el usuario nunca llegue a ver ese rechazo.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** El límite del SAT, el mismo que valida el fiscal-engine. */
const SAT_LIMIT_MS = 72 * HOUR_MS;
/** Lo más atrás que se ofrece elegir: una hora antes del límite, para revisar y timbrar sin cruzarlo. */
const PICK_LIMIT_MS = 71 * HOUR_MS;
/** Lo que se le descuenta al límite al timbrar: el reloj del dispositivo contra el del servidor y la latencia. */
const STAMP_MARGIN_MS = 5 * MINUTE_MS;

const OFFERED_DAYS = [0, 1, 2, 3];

export type BackdateProblem = 'expired' | 'future';

/** Por qué la fecha elegida ya no se puede timbrar, o `null`. «Hoy, al timbrar» nunca tiene problema. */
export const backdateProblem = (
  backdatedTo: string | undefined,
  now = new Date(),
): BackdateProblem | null => {
  if (!backdatedTo) return null;
  const at = Date.parse(backdatedTo);
  if (Number.isNaN(at) || now.getTime() - at > SAT_LIMIT_MS - STAMP_MARGIN_MS) return 'expired';
  return at > now.getTime() ? 'future' : null;
};

/** El `date` de `POST /cfdi/emit`: la elegida, o la de este momento. Una caducada viaja tal cual y el backend la rechaza. */
export const invoiceEmissionDate = (draft: Pick<InvoiceDraft, 'backdatedTo'>): string =>
  draft.backdatedTo ?? emissionDate();

/** Cuántas horas atrás, con un decimal. 0 es «hoy, al timbrar». */
export const backdatedHoursOf = (draft: Pick<InvoiceDraft, 'backdatedTo'>, now = new Date()): number => {
  const at = draft.backdatedTo ? Date.parse(draft.backdatedTo) : Number.NaN;
  if (Number.isNaN(at)) return 0;
  return Math.max(0, Math.round(((now.getTime() - at) / HOUR_MS) * 10) / 10);
};

/** Al retomar un borrador guardado, una fecha que ya no se puede timbrar regresa a hoy. */
export const withoutExpiredBackdate = (
  draft: InvoiceDraft,
  now = new Date(),
): { draft: InvoiceDraft; wasReset: boolean } =>
  backdateProblem(draft.backdatedTo, now)
    ? { draft: { ...draft, backdatedTo: undefined }, wasReset: true }
    : { draft, wasReset: false };

const dayStart = (reference: Date, daysAgo: number): Date =>
  new Date(reference.getFullYear(), reference.getMonth(), reference.getDate() - daysAgo);

/** Lo elegible, a minuto cerrado: nada antes de 71 h ni después de este momento. */
const pickWindow = (now: Date) => ({
  earliest: Math.ceil((now.getTime() - PICK_LIMIT_MS) / MINUTE_MS) * MINUTE_MS,
  latest: Math.floor(now.getTime() / MINUTE_MS) * MINUTE_MS,
});

const pad = (value: number) => String(value).padStart(2, '0');

const toTimeValue = (ms: number): string => {
  const date = new Date(ms);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const WEEKDAY_FORMAT = {
  long: new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric' }),
  short: new Intl.DateTimeFormat('es-MX', { weekday: 'short', day: 'numeric' }),
};
const TIME_FORMAT = new Intl.DateTimeFormat('es-MX', { hour: 'numeric', minute: '2-digit' });

const dayLabel = (daysAgo: number, now: Date, width: 'long' | 'short'): string => {
  if (daysAgo === 0) return 'Hoy';
  if (daysAgo === 1) return 'Ayer';
  return capitalize(WEEKDAY_FORMAT[width].format(dayStart(now, daysAgo)));
};

export interface EmissionDay {
  daysAgo: number;
  label: string;
  shortLabel: string;
}

/** Hoy y hasta tres días atrás, mientras alguna hora de ese día quepa en la ventana. */
export const emissionDays = (now = new Date()): EmissionDay[] => {
  const { earliest } = pickWindow(now);
  return OFFERED_DAYS.filter((daysAgo) => dayStart(now, daysAgo - 1).getTime() > earliest).map(
    (daysAgo) => ({
      daysAgo,
      label: dayLabel(daysAgo, now, 'long'),
      shortLabel: dayLabel(daysAgo, now, 'short'),
    }),
  );
};

/** Días de calendario entre hoy y la fecha elegida, en la hora del dispositivo. */
export const emissionDaysAgo = (backdatedTo: string | undefined, now = new Date()): number =>
  backdatedTo
    ? Math.round((dayStart(now, 0).getTime() - dayStart(new Date(backdatedTo), 0).getTime()) / DAY_MS)
    : 0;

/** `HH:MM` del campo de hora: la elegida, o la de este momento si es hoy al timbrar. */
export const emissionTimeValue = (backdatedTo: string | undefined, now = new Date()): string =>
  toTimeValue(backdatedTo ? Date.parse(backdatedTo) : now.getTime());

/**
 * Primera y última hora elegibles de ese día, para el `min`/`max` del campo. `cutBySat`
 * dice que el límite parte el día: es lo único que vale la pena explicarle al usuario.
 */
export const emissionTimeBounds = (daysAgo: number, now = new Date()) => {
  const { earliest, latest } = pickWindow(now);
  const start = dayStart(now, daysAgo).getTime();
  const end = dayStart(now, daysAgo - 1).getTime() - MINUTE_MS;
  const first = Math.max(start, earliest);
  return {
    min: toTimeValue(first),
    max: toTimeValue(Math.min(end, latest)),
    cutBySat: earliest > start,
    firstLabel: TIME_FORMAT.format(first),
  };
};

/**
 * El instante de «hace N días a las HH:MM», llevado a la ventana: iOS no respeta el
 * `min`/`max` de un campo de hora. `undefined` es hoy al timbrar, porque la hora de este
 * momento (o una posterior) no es retroactiva.
 */
export const backdateFor = (daysAgo: number, time: string, now = new Date()): string | undefined => {
  const [hours, minutes] = time.split(':').map(Number);
  const day = dayStart(now, daysAgo);
  const at = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hours, minutes).getTime();
  const { earliest, latest } = pickWindow(now);
  if (Number.isNaN(at) || at >= latest) return undefined;
  return new Date(Math.max(at, earliest)).toISOString();
};

/** La fecha en una línea: «Hoy, al timbrar», «Ayer, 3:30 p.m.», «Domingo 28, 9:00 a.m.». */
export const describeEmissionDate = (backdatedTo: string | undefined, now = new Date()): string =>
  backdatedTo
    ? `${dayLabel(emissionDaysAgo(backdatedTo, now), now, 'long')}, ${TIME_FORMAT.format(new Date(backdatedTo))}`
    : 'Hoy, al timbrar';
